import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import {
  QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS,
  useListScheduledQueryConversations,
} from '~/react-query-hooks/alfred/useScheduledQueries';
import { useScheduledTaskView } from '~/stores/use-scheduled-task-view/useScheduledTaskView';
import { Conversation } from '~/views/alfred/research/use-chat/use-chat.types';
import { useChat } from '~/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { QUERY_ALL_CONVERSATIONS } from './useFetchInfiniteConversation';

const OVERALL_TIMEOUT_MS = 60_000;

// web-app learns a run has landed via a websocket push
// (useSubscribeToRunResult) which delivers the full conversation directly;
// lseg has neither the websocket nor a general list endpoint that reliably
// reflects a brand-new conversation right away (re-requesting it repeatedly
// after the run was too flaky - unknown/variable backend indexing delay).
// Instead: detect the run's conversation via the scheduled task's own
// (fast-polled) run-history endpoint - confirmed reliable - then build a
// minimal Conversation record straight from that response and append it to
// the sidebar's local state directly, so it shows up instantly regardless
// of the general list. The general list is still invalidated in the
// background so a later real fetch can fill in fields this can't know
// (summary, access_level, etc.) - ceiling: if that refetch resolves before
// this local id is deduped away, minor fields stay as their defaults until
// a full page reload. Mounted once at the sidebar level so it survives
// navigating away while waiting. Upgrade path: port the websocket push.
export const useScheduledTaskRunSync = () => {
  const queryClient = useQueryClient();
  const { navigateToConversation } = useChatNavigation();
  const {
    pendingRunTaskId,
    setPendingRunTaskId,
    setSelectedScheduledQueryId,
    setCategory,
  } = useScheduledTaskView(state => ({
    pendingRunTaskId: state.pendingRunTaskId,
    setPendingRunTaskId: state.setPendingRunTaskId,
    setSelectedScheduledQueryId: state.setSelectedScheduledQueryId,
    setCategory: state.setCategory,
  }));

  const { data, isLoading, isPlaceholderData } =
    useListScheduledQueryConversations(
      pendingRunTaskId ?? '',
      {},
      !!pendingRunTaskId,
      true
    );
  const runConversations = data?.conversations ?? [];


  const taskRef = useRef<string | null>(null);
  const knownRunIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (taskRef.current !== pendingRunTaskId) {
      taskRef.current = pendingRunTaskId;
      const cached = pendingRunTaskId
        ? queryClient.getQueryData<{ conversations: { id: string }[] }>([
            QUERY_KEY_SCHEDULED_QUERY_CONVERSATIONS,
            pendingRunTaskId,
            {},
          ])
        : undefined;
      knownRunIdsRef.current = cached
        ? new Set(cached.conversations.map(conv => conv.id))
        : null;
    }

    if (!pendingRunTaskId || isLoading || isPlaceholderData) {
      return;
    }

    const ids = runConversations.map(conv => conv.id);

    if (!knownRunIdsRef.current) {
      knownRunIdsRef.current = new Set(ids);
      return;
    }

    const newConv = runConversations.find(
      conv => !knownRunIdsRef.current?.has(conv.id)
    );
    knownRunIdsRef.current = new Set(ids);

    if (!newConv) {
      return;
    }

    const now = new Date().toISOString();

    if (!useChat.getState().conversations.some(c => c.id === newConv.id)) {
      const conversation: Conversation = {
        id: newConv.id,
        name: newConv.name,
        date: newConv.updated_at ?? now,
        created_date: newConv.updated_at ?? now,
        summary: '',
        favourite: newConv.favourite ?? false,
        access_level: 'private',
        status: 'Processing',
        scheduled_query_id: pendingRunTaskId,
        active: true,
        public_copies: [],
      };

      useChat.getState().appendNewConversation(conversation);
    }

    useChat.getState().setAnalysisMode('Deep');
    setSelectedScheduledQueryId(null);
    setCategory('all');
    navigateToConversation(newConv.id);
    setPendingRunTaskId(null);
    queryClient.invalidateQueries({ queryKey: [QUERY_ALL_CONVERSATIONS] });
  }, [
    pendingRunTaskId,
    runConversations,
    isLoading,
    isPlaceholderData,
    queryClient,
    navigateToConversation,
    setSelectedScheduledQueryId,
    setCategory,
    setPendingRunTaskId,
  ]);

  useEffect(() => {
    if (!pendingRunTaskId) {
      return;
    }

    const timeout = setTimeout(
      () => setPendingRunTaskId(null),
      OVERALL_TIMEOUT_MS
    );
    return () => clearTimeout(timeout);
  }, [pendingRunTaskId, setPendingRunTaskId]);
};
